import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Divider, Tooltip } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import { CircularProgress, IconButton } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { differenceInDays, startOfDay } from "date-fns";
import { useOperationDespatchContext } from "../../../../../../../../contexts/DespatchContext";
import { useDespatchActions } from "../../../../hooks/useDespatchActions";
import { CommentsCount } from "../../../../style";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";
import { IconMap } from "Components/Generic/Icons/IconMap";
import { IconMoreVertical } from "Components/Generic/Icons/IconMoreVertical";
import { IconOpenOrder } from "Components/Generic/Icons/IconOpenOrder";
import { IconPrint } from "Components/Generic/Icons/IconPrint";
import { OverflownTooltip } from "Components/Generic/Tooltip/OverflownTooltip";
import { VipChip } from "Components/Generic/VipChip";
import {
  DESTINATION_TASK_TYPES,
  GO_SHARE_COMPANY_IDS,
  GOVLOG_COMPANY_NAME,
  PRIVATE_ROUTES,
} from "Constants";
import { useAppState } from "Context/StateContext";
import { CloseCircle as IconCloseCircle, Trash as DeleteIcon } from "iconsax-react";
import { DespatchTask } from "Models/despatch.task";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { checkResourceAvailability } from "Util/checkAvailability";
import { formatDecimalToWhole, handleGetConvertValue } from "Util/converts";
import {
  EquipmentType,
  ResourceName,
  ResourceSubGroup,
  TaskStatus,
  TaskType as TaskTypeEnum,
} from "Util/enums";
import { getCompanyColorAndBackgroundColorById } from "Util/getCompanyColorByName";
import { getMeasurement, getWeightMeasurement } from "Util/getMeasurement";
import {
  checkPublishEnabledTaskStatus,
  isPlaceholderTaskEnabledToDelete,
  isTaskEnabledToCancel,
  isTaskEnabledToSeeMoreOptions,
  isTaskEnabledToTimeControl,
  isTaskFinished,
} from "Util/operation.despatch";
import { operationsPermissions } from "Util/permissions";
import { getCompleteDateRangeLabel } from "Util/startDate";

import { ResourceSlot, ResourceSlotSubItem } from "../../../ResourceSlot";
import { PublishTaskIcon, UnpublishTaskIcon } from "../../ResourceTeamViewer/TeamCard/styled";
import { DNDFieldsNames, generateSlotSourceId, DNDSlotTypes } from "../../useDragSystem/slotTypes";
import {
  AccordionHeader,
  ActionsFooter,
  BookingDetails,
  BoxDetails,
  CardAccordion,
  CardAccordionDetails,
  CardAccordionSummary,
  CardChip,
  CardHeader,
  CardHeaderCompanySection,
  CardHeaderDetailsSection,
  CardHeaderDetailsSectionLine,
  CardItemSection,
  CardRoot,
  CommentsIconWrapper,
  CustomerAddress,
  CustomerName,
  FooterBox,
  Heading,
  IconTypo,
  InfoDetails,
  MenuItemText,
  MoreOptionsMenu,
  OptionMenu,
  ParentBox,
  TaskDescription,
  TimeInfo,
  TimeSection,
  MemberLeaderWrapper,
  MemberLeaderLabel,
  MemberLeaderNameBadge,
  useTaskCardStyles,
} from "./styles";
import { Task, TaskStartDatePeriod } from "Models/task";
import { UNPUBLISHED_TASKS } from "Modules/moving/components/Diary/shared/DespatchFilters/types";
import { compareText } from "Util/resourceSorting";
import { StorageBox } from "Models/storage.box";

const cubeIcon = require("Assets/icons/3d-cube-scan.svg");
const boxIcon = require("Assets/icons/box.svg");
const clockIcon = require("Assets/icons/clock.svg");
const messageIcon = require("Assets/icons/message.png");
const weightIcon = require("Assets/icons/weight.svg");

interface TaskCardProps {
  task: DespatchTask;
  onRemoveElement: (removedId: string, id?: number) => void;
}

const isDestinationTaskType = (type?: string) =>
  !!type?.toLowerCase().trim() &&
  DESTINATION_TASK_TYPES.some(
    (destinationType) => destinationType.toLowerCase().trim() === type.toLowerCase().trim()
  );

const TaskCard: React.FC<TaskCardProps> = ({ task, onRemoveElement }) => {
  const { currentUser, userTimezone } = useAppState();

  const measurement = getMeasurement(task.company.measurement);
  const weightMeasurement = getWeightMeasurement(task.company.measurementWeight);
  const { t: translate } = useTranslation();
  const { currentDraggable } = useOperationDespatchContext();
  const classes = useTaskCardStyles();

  const {
    expandedOrdersTasks,
    toggleOrderTaskExpanded,
    currentDate,
    updateCrewTaskValueByTaskId,
    taskCommentRef,
    despatchTimeControlRef,
    cancelTaskRef,
    deletePlaceholderTaskRef,
    changeTimeRef,
  } = useOperationDespatchContext();
  const { usePublishTask, useUnpublishTask } = useDespatchActions();

  const isUserEnableToEdit = operationsPermissions({
    subGroup: ResourceSubGroup.DISPATCH,
    resourceName: ResourceName.DISPATCH,
    currentUser,
  })?.edit;
  const isUserEnableToEditOrders = operationsPermissions({ currentUser })?.edit;

  const onSuccessPublishingTask = (task: DespatchTask, taskStatus: string) => {
    updateCrewTaskValueByTaskId(task.id, "status", taskStatus);
  };
  const { mutate: publishTask, isLoading: isLoadingPublish } = usePublishTask((task) =>
    onSuccessPublishingTask(task, TaskStatus.PENDING_ACCEPT)
  );
  const { mutate: unpublishTask, isLoading: isLoadingUnpublish } = useUnpublishTask((task) =>
    onSuccessPublishingTask(task, TaskStatus.REGISTERED)
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const generatorId = (slotName: DNDSlotTypes) => {
    return generateSlotSourceId(task.id, "slot", DNDFieldsNames.CARD_ORDER, slotName);
  };
  const history = useHistory();

  const isVip = !!task.job.vipTreatment;

  const jobTypeName = task?.job?.jobType?.name ?? task?.job?.type;

  const customerName = useMemo(() => {
    const customerAbbreviation = task?.job?.customer?.abbreviation || "";
    const customerName = task?.job?.customer?.fullName || "";

    return `${customerAbbreviation} ${customerName}`.trim();
  }, [task]);

  const customerAddress = useMemo(() => {
    if (!task?.job) return null;

    const place = isDestinationTaskType(task?.type)
      ? task.job.destinations?.[0]
      : task.job.origins?.[0];

    return place?.longAddress;
  }, [task?.type, task?.job?.destinations, task?.job?.origins]);

  const dateRanged = useMemo(() => {
    return getCompleteDateRangeLabel({
      startDate: task.startDate,
      endDate: task.endDate,
      separator: "-",
      currentDate,
      timeSlots: task.timeSlots,
      period: task.period,
      timezone: userTimezone,
      preferences: currentUser?.preferences,
    });
  }, [task, currentDate, currentUser?.preferences, userTimezone]);

  const volumeLabel = `${handleGetConvertValue(
    task?.totalVolumeQuoted || task?.totalVolumeActual,
    measurement.code,
    measurement.decimalPlaces
  )} ${measurement.code}`;

  const weightQuotedAndActualLabel = `${formatDecimalToWhole(
    handleGetConvertValue(
      task?.totalWeightQuoted,
      weightMeasurement.code,
      weightMeasurement.decimalPlaces
    )
  )}/${formatDecimalToWhole(
    handleGetConvertValue(
      task?.totalWeightActual,
      weightMeasurement.code,
      weightMeasurement.decimalPlaces
    )
  )} ${weightMeasurement.code}`;

  const [isAccordionOpened, setIsAccordionOpened] = useState(false);
  const isExpanded = useMemo(() => {
    return expandedOrdersTasks.includes(task.id);
  }, [expandedOrdersTasks]);

  const taskEnabledToPublishOrUnpublish = useMemo(() => {
    return checkPublishEnabledTaskStatus(task);
  }, [task]);

  const canEditResources =
    !!isUserEnableToEdit && UNPUBLISHED_TASKS.includes(task.status as TaskStatus);

  const taskEnabledToCancel = useMemo(() => {
    return isTaskEnabledToCancel(task);
  }, [task]);

  const placeholderTaskEnabledToDelete = useMemo(() => {
    return isPlaceholderTaskEnabledToDelete(task);
  }, [task]);

  const enabledToSeeMoreOptions = useMemo(() => {
    return isTaskEnabledToSeeMoreOptions(task);
  }, [task]);

  const taskEnabledToTimeControl = useMemo(() => {
    return isTaskEnabledToTimeControl(task);
  }, [task]);

  const isFinished = useMemo(() => isTaskFinished(task), [task]);

  useEffect(() => {
    setIsAccordionOpened(isExpanded);
  }, [isExpanded]);

  const managePublishing = () => {
    if (!taskEnabledToPublishOrUnpublish) return;

    if (taskEnabledToPublishOrUnpublish === "publish") {
      return publishTask({
        taskId: task.id,
        task,
      });
    }

    return unpublishTask({
      taskId: task.id,
      task,
    });
  };

  const renderPublishIcon = () => {
    if (!taskEnabledToPublishOrUnpublish) return;

    if (isLoadingPublish || isLoadingUnpublish) return <CircularProgress size={24} />;

    return taskEnabledToPublishOrUnpublish === "publish" ? (
      <PublishTaskIcon color="currentColor" />
    ) : (
      <UnpublishTaskIcon color="currentColor" />
    );
  };

  const renderHeaderSubsectionWithJobInfo = () => {
    if (!jobTypeName) return;

    return (
      <CardHeaderDetailsSectionLine spaceBetween>
        <CardChip>{jobTypeName}</CardChip>

        <BadgeJobStatus
          value={task?.isPlaceholder ? translate("placeholder") : task.status}
          className={classes.statusChip}
        />
      </CardHeaderDetailsSectionLine>
    );
  };
  const renderLiftvanOrItemQuantity = useCallback(() => {
    const companyHasToShowLiftvan = !!GO_SHARE_COMPANY_IDS.includes(task?.companyId!);

    if (companyHasToShowLiftvan) {
      return (
        <InfoDetails>
          {task?.totalLiftvans}{" "}
          {task?.totalLiftvans > 1 ? translate("liftvans") : translate("liftvan")}
        </InfoDetails>
      );
    }

    return (
      <InfoDetails>
        {task?.totalItems} {translate("qty")}
      </InfoDetails>
    );
  }, [task]);

  const taskTitle = useMemo(() => {
    if (task?.isPlaceholder) return task?.name;

    return `#${task?.job?.reference} - ${task.type}`;
  }, [task]);

  const team = useMemo(() => {
    if (task.team) return task.team;

    if (task.teams?.length) {
      return task.teams?.find((t) => {
        const createdTo = new Date(t.createdTo!);
        const differenceBetween = differenceInDays(startOfDay(currentDate), startOfDay(createdTo));
        if (differenceBetween === 0) return t;

        return null;
      });
    }
  }, [task, task.teams, currentDate]);

  const sortedMembers = useMemo(() => {
    const members = team?.members ?? [];
    return [...members].sort((a, b) => compareText(a.fullName ?? "", b.fullName ?? ""));
  }, [team?.members]);

  const getEquipmentSort = (equipment: any) => {
    if (equipment.equipmentType === EquipmentType.GENERAL) return equipment.description ?? "";
    return String(equipment.fleetNumber ?? "");
  };

  const sortedFleets = useMemo(() => {
    const fleets = team?.fleets ?? [];
    return [...fleets].sort((a, b) => compareText(getEquipmentSort(a), getEquipmentSort(b)));
  }, [team?.fleets]);

  const sortedStorageBoxes = useMemo(() => {
    const storageBoxes = task.storageBoxes ?? [];

    const getSortFullTextKey = (box: Pick<StorageBox, "number" | "storageBoxType">) => {
      const typeName = box.storageBoxType?.name?.trim() ?? "";
      const number = String(box.number ?? "").trim();
      return `${typeName} #${number}`.trim();
    };

    return [...storageBoxes].sort((a, b) =>
      compareText(getSortFullTextKey(a), getSortFullTextKey(b))
    );
  }, [task.storageBoxes]);

  const renderTaskLeaderName = useMemo(() => {
    if (!task.memberLeaderId) return null;

    return (
      <MemberLeaderWrapper>
        <MemberLeaderLabel>{translate("leader")}:</MemberLeaderLabel>
        <MemberLeaderNameBadge>
          <span>{task.memberLeader?.fullName}</span>
        </MemberLeaderNameBadge>
      </MemberLeaderWrapper>
    );
  }, [task, task.memberLeader]);

  return (
    <CardRoot>
      <CardHeader isVip={false} isPlaceholder={task?.isPlaceholder}>
        <CardHeaderCompanySection
          sx={{
            backgroundColor: getCompanyColorAndBackgroundColorById(task.companyId, {
              isVip: false,
              isPlaceholder: task?.isPlaceholder,
            })?.backgroundColor,
          }}
        >
          <CircleIcon
            sx={{
              width: "8px !important",
              height: "8px !important",
              color: getCompanyColorAndBackgroundColorById(task.companyId, {
                isVip: false,
                isPlaceholder: task?.isPlaceholder,
              })?.color,
            }}
          />
          {task.company.name}
        </CardHeaderCompanySection>

        <Divider />

        <CardHeaderDetailsSection>
          {renderHeaderSubsectionWithJobInfo()}

          <CardHeaderDetailsSectionLine>
            {isVip && <VipChip vipTreatment={task.job.vipTreatment!} />}

            <Heading>
              <OverflownTooltip text={taskTitle}>{taskTitle}</OverflownTooltip>
            </Heading>

            {!jobTypeName && (
              <BadgeJobStatus
                value={task?.isPlaceholder ? translate("placeholder") : task.status}
                className={classes.statusChip}
              />
            )}
          </CardHeaderDetailsSectionLine>
        </CardHeaderDetailsSection>
      </CardHeader>
      <CardAccordion
        elevation={0}
        expanded={isAccordionOpened}
        onChange={(_event, expanded) => {
          setIsAccordionOpened(expanded);
        }}
      >
        <CardAccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          onClick={() => toggleOrderTaskExpanded(task.id)}
          isVip={false}
          isPlaceholder={task?.isPlaceholder}
        >
          <AccordionHeader>
            <BookingDetails>
              <CustomerName>{customerName}</CustomerName>
              <CustomerAddress>{customerAddress}</CustomerAddress>
              <TaskDescription>{task?.description}</TaskDescription>

              {renderTaskLeaderName}

              {task.job?.branchFrom && (
                <MemberLeaderWrapper key={`${task.job.branchFrom.name}-task.job?.branchFrom`}>
                  <MemberLeaderLabel>{translate("branchOrigin")}:</MemberLeaderLabel>
                  <MemberLeaderNameBadge>
                    <span>{task.job.branchFrom.name}</span>
                  </MemberLeaderNameBadge>
                </MemberLeaderWrapper>
              )}

              {task.job?.toBranch && (
                <MemberLeaderWrapper key={`${task.job.toBranch.name}-task.job?.toBranch`}>
                  <MemberLeaderLabel>{translate("branchDestination")}:</MemberLeaderLabel>
                  <MemberLeaderNameBadge>
                    <span>{task.job.toBranch.name}</span>
                  </MemberLeaderNameBadge>
                </MemberLeaderWrapper>
              )}

              <TimeSection
                disabled={!isUserEnableToEdit}
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  event.stopPropagation();
                  if (!isUserEnableToEdit) return null;
                  changeTimeRef.current?.open(task);
                }}
              >
                <AccessTimeIcon className={classes.iconSpacing} />
                <TimeInfo>{dateRanged}</TimeInfo>
              </TimeSection>
            </BookingDetails>

            {!task?.isPlaceholder ? (
              <>
                <Divider className={classes.fullWidthDivider} />
                <BoxDetails>
                  <IconTypo>
                    <img
                      src={cubeIcon}
                      alt=""
                      width={16}
                      height={16}
                      className={classes.iconSpacing}
                    />
                    <InfoDetails>{volumeLabel}</InfoDetails>
                  </IconTypo>
                  <IconTypo>
                    <img
                      src={weightIcon}
                      alt=""
                      width={16}
                      height={16}
                      className={classes.iconSpacing}
                    />
                    <InfoDetails>{weightQuotedAndActualLabel}</InfoDetails>
                  </IconTypo>
                  <IconTypo>
                    <img
                      src={boxIcon}
                      alt=""
                      width={16}
                      height={16}
                      className={classes.iconSpacing}
                    />
                    {renderLiftvanOrItemQuantity()}
                  </IconTypo>
                </BoxDetails>
              </>
            ) : null}
          </AccordionHeader>
        </CardAccordionSummary>

        <CardAccordionDetails>
          <ResourceSlot
            id={generatorId(DNDSlotTypes.ORDER_TEAM)}
            disableGutters
            wrapPlaceHolder={false}
            isDropDisabled={!isAccordionOpened || !canEditResources}
            isDropPlaceHolderDisabled
          >
            <CardItemSection>
              <ResourceSlot
                isDropDisabled={!isAccordionOpened || !canEditResources}
                title={
                  <ResourceSlotSubItem
                    deleteButtonProps={{
                      disabled: isFinished || !canEditResources,
                      disabledTooltip: isFinished
                        ? translate("theTaskIsFinishedSoItCannotBeRemoved")
                        : !canEditResources
                        ? translate("taskUnpublishedCannotBeEdited")
                        : undefined,
                    }}
                    hasDeleteButton={canEditResources}
                    disableHouver={!!currentDraggable}
                    onDeleteClick={() => onRemoveElement(generatorId(DNDSlotTypes.ORDER_LEADER))}
                    text={team?.memberLeader?.fullName}
                  />
                }
                id={generatorId(DNDSlotTypes.ORDER_LEADER)}
                active={!!team?.memberLeader}
                iconName="crown"
                placeHolder={translate("noLeaderAdded")}
              />

              <ResourceSlot
                isDropDisabled={!isAccordionOpened || !canEditResources}
                title={sortedMembers.map((member) => {
                  const memberIsNoLongerAvailable =
                    (!!member.endEmploymentDate &&
                      differenceInDays(currentDate, new Date(member.endEmploymentDate)) > 0) ||
                    (!!member.startEmploymentDate &&
                      differenceInDays(new Date(member.startEmploymentDate), currentDate) < 0);

                  return (
                    <ResourceSlotSubItem
                      key={member.id}
                      hasDeleteButton={canEditResources}
                      deleteButtonProps={{
                        disabled: isFinished || !canEditResources,
                        disabledTooltip: isFinished
                          ? translate("theTaskIsFinishedSoItCannotBeRemoved")
                          : !canEditResources
                          ? translate("taskUnpublishedCannotBeEdited")
                          : undefined,
                      }}
                      disableHouver={!!currentDraggable}
                      onDeleteClick={() =>
                        onRemoveElement(generatorId(DNDSlotTypes.ORDER_STAFFS), member.id)
                      }
                      text={member.fullName}
                      hasError={memberIsNoLongerAvailable}
                      hasTooltip={memberIsNoLongerAvailable}
                      tooltipText={translate("thisMemberIsUnavailableOnThisDate")}
                    />
                  );
                })}
                iconName="people"
                id={generatorId(DNDSlotTypes.ORDER_STAFFS)}
                active={!!sortedMembers.length}
                placeHolder={translate("noStaffAdded")}
              />

              <ResourceSlot
                isDropDisabled={!isAccordionOpened || !canEditResources}
                title={sortedFleets.map((equipment) => {
                  const hasError = !checkResourceAvailability(
                    currentDate,
                    equipment.availabilities,
                    userTimezone.name
                  ).isAvailable;

                  const equipmentName =
                    equipment.equipmentType === EquipmentType.GENERAL
                      ? equipment.description
                      : `#${equipment.fleetNumber}`;

                  return (
                    <ResourceSlotSubItem
                      key={equipment.id}
                      hasDeleteButton={canEditResources}
                      deleteButtonProps={{
                        disabled: isFinished || !canEditResources,
                        disabledTooltip: isFinished
                          ? translate("theTaskIsFinishedSoItCannotBeRemoved")
                          : !canEditResources
                          ? translate("taskUnpublishedCannotBeEdited")
                          : undefined,
                      }}
                      disableHouver={!!currentDraggable}
                      hasError={hasError}
                      hasTooltip={hasError}
                      tooltipText={translate("thisFleetIsUnavailableOnThisDate")}
                      onDeleteClick={() =>
                        onRemoveElement(generatorId(DNDSlotTypes.ORDER_EQUIPMENTS), equipment.id)
                      }
                      title={equipment.equipmentType}
                      text={equipmentName}
                    />
                  );
                })}
                id={generatorId(DNDSlotTypes.ORDER_EQUIPMENTS)}
                iconName="truck"
                active={!!sortedFleets.length}
                placeHolder={translate("noEquipmentAdded")}
              />

              <ResourceSlot
                isDropDisabled={!isAccordionOpened || !canEditResources}
                title={sortedStorageBoxes.map((storageBox) => (
                  <ResourceSlotSubItem
                    key={storageBox.id}
                    hasDeleteButton={canEditResources}
                    deleteButtonProps={{
                      disabled: isFinished || !canEditResources,
                      disabledTooltip: isFinished
                        ? translate("theTaskIsFinishedSoItCannotBeRemoved")
                        : !canEditResources
                        ? translate("taskUnpublishedCannotBeEdited")
                        : undefined,
                    }}
                    disableHouver={!!currentDraggable}
                    onDeleteClick={() =>
                      onRemoveElement(generatorId(DNDSlotTypes.ORDER_STORAGE_BOXES), storageBox?.id)
                    }
                    title={storageBox.storageBoxType?.name}
                    text={`#${storageBox.number}`}
                  />
                ))}
                id={generatorId(DNDSlotTypes.ORDER_STORAGE_BOXES)}
                iconName="container"
                active={!!sortedStorageBoxes.length}
                placeHolder={translate("noStorageBoxAdded")}
              />
            </CardItemSection>
          </ResourceSlot>

          <ActionsFooter isVip={false} isPlaceholder={task?.isPlaceholder}>
            <ParentBox>
              <FooterBox>
                {taskEnabledToPublishOrUnpublish && isUserEnableToEdit && (
                  <Tooltip
                    title={
                      taskEnabledToPublishOrUnpublish === "publish"
                        ? translate("publishTask")
                        : translate("unpublishTask")
                    }
                  >
                    <IconButton
                      onClick={() => {
                        managePublishing();
                      }}
                      color="inherit"
                    >
                      {renderPublishIcon()}
                    </IconButton>
                  </Tooltip>
                )}
                {taskEnabledToTimeControl && (
                  <Tooltip title={translate("Time control")}>
                    <IconButton
                      onClick={() => {
                        despatchTimeControlRef?.current?.open(task?.id, isUserEnableToEdit);
                      }}
                    >
                      <img src={clockIcon} alt="clockIcon" />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title={translate("comments")}>
                  <IconButton
                    onClick={() => {
                      taskCommentRef.current?.open(task);
                    }}
                  >
                    <CommentsIconWrapper>
                      <img src={messageIcon} alt="messageIcon" />
                      {task?.comments && task?.comments?.length > 0 && (
                        <CommentsCount>{task.comments.length}</CommentsCount>
                      )}
                    </CommentsIconWrapper>
                  </IconButton>
                </Tooltip>

                {taskEnabledToCancel && isUserEnableToEdit && (
                  <Tooltip title={translate("Cancel task")}>
                    <IconButton
                      onClick={() => {
                        cancelTaskRef.current?.open(task);
                      }}
                    >
                      <IconCloseCircle size={24} color={colors.error500} />
                    </IconButton>
                  </Tooltip>
                )}

                {placeholderTaskEnabledToDelete && isUserEnableToEdit && (
                  <Tooltip title={translate("deleteTask")}>
                    <IconButton
                      onClick={() => {
                        deletePlaceholderTaskRef.current?.open(task);
                      }}
                    >
                      <DeleteIcon size={24} color={colors.error500} />
                    </IconButton>
                  </Tooltip>
                )}
              </FooterBox>

              {enabledToSeeMoreOptions ? (
                <IconButton
                  size="small"
                  onClick={(event: React.MouseEvent<HTMLElement>) =>
                    setAnchorEl(event.currentTarget)
                  }
                >
                  <IconMoreVertical height={20} width={20} color={colors.gray600} />
                </IconButton>
              ) : null}
            </ParentBox>
          </ActionsFooter>

          <MoreOptionsMenu
            id="settings-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            MenuListProps={{
              "aria-labelledby": "settings-menu-button",
              role: "listbox",
            }}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            {task?.job?.id && (isUserEnableToEdit || isUserEnableToEditOrders) ? (
              <OptionMenu
                onClick={() => {
                  const url = `${PRIVATE_ROUTES.ORDERS_MNT}/${task?.job?.id}`;
                  window.open(url, "_blank");
                }}
              >
                <IconOpenOrder height={24} width={24} color={colors.gray600} />
                <MenuItemText>{translate("openOrder")}</MenuItemText>
              </OptionMenu>
            ) : null}
            {task?.job?.id && (isUserEnableToEdit || isUserEnableToEditOrders) ? <Divider /> : null}

            <OptionMenu disabled>
              <IconMap height={24} width={24} color={colors.gray600} />
              <MenuItemText>{translate("accessDetails")}</MenuItemText>
            </OptionMenu>

            <OptionMenu disabled>
              <IconPrint height={24} width={24} color={colors.gray600} />
              <MenuItemText>{translate("generateReport")}</MenuItemText>
            </OptionMenu>
          </MoreOptionsMenu>
        </CardAccordionDetails>
      </CardAccordion>
    </CardRoot>
  );
};

export { TaskCard };
